import React, { FC, useEffect, useMemo, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useCheckTikTokCheckoutStepIsAllowed } from 'src/Pages/Artist/TiktokSchedulePage/helpers/useCheckTikTokCheckoutStepIsAllowed'
import { usePaymentCancelledTikTokEvents } from 'src/Hooks/usePaymentCancelledTikTokEvents'
import { SchedulePaymentMethod } from 'src/Components/SchedulePaymentMethod'
import { ScheduleSummary } from 'src/Components/ScheduleSummary'
import { ScheduleCheckoutErrors } from 'src/Components/ScheduleCheckoutErrors'
import { SchedulePaymentProcessingModal } from 'src/Components/SchedulePaymentProcessingModal'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getIsProcessingPage } from 'src/Redux/paypalTransactions-process/selectors'
import {
  getPaymentAlertError,
  getPaymentMethod,
} from 'src/Redux/payment-process/selectors'
import { setPaymentMethod } from 'src/Redux/payment-process'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useReels } from 'src/Hooks/useReels'
import { useReelsEvents } from 'src/Hooks/Analytics/useReelsEvents'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { useWalletPaymentMethods } from 'src/Hooks/useWalletPaymentMethods'
import {
  getDisplayedPaymentMethods,
  getFallbackPaymentMethod,
  getIsConfirmDisabled,
} from 'src/Utils/displayedPaymentMethods'

import { useTikTokSchedulePaymentMethod } from './useTikTokSchedulePaymentMethod'
import { ScheduleReelsModal } from './ScheduleReelsModal'
import { useTikTokScheduleOrderSummary } from './useTikTokScheduleOrderSummary'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    align-items: flex-start;
    flex-direction: row;
    align-self: stretch;
  }
`

export const TiktokScheduleOrderCheckoutPage: FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isReelsModalOpen, setIsReelsModalOpen] = useState(false)
  const [isWalletProcessing, setIsWalletProcessing] = useState(false)

  useCheckTikTokCheckoutStepIsAllowed()
  usePaymentCancelledTikTokEvents()

  const tiktokSchedulePaymentMethodProps = useTikTokSchedulePaymentMethod()
  const {
    availablePaymentMethods,
    onConfirm,
    isOrderFullyCovered,
    isSyncing,
    isWalletPaymentEligible,
    walletAmount,
    onConfirmWalletPayment,
    onWalletPaymentSuccess,
    onWalletPaymentError,
    ...tiktokScheduleSummaryProps
  } = useTikTokScheduleOrderSummary()

  const { isReelsCampaign, onUnsetReelsFeature } = useReels()
  const { onReelsConfirmEvent, onReelsTikTokTried, onReelsTikTokNotNow } =
    useReelsEvents()

  const paymentError = useAppSelector(getPaymentAlertError)
  const isProcessingPageVisible = useAppSelector(getIsProcessingPage)
  const paymentMethod = useAppSelector(getPaymentMethod)
  const scheduledCampaign = useAppSelector(getScheduledTikTokCampaign)
  const trackCover =
    scheduledCampaign.track?.coverUrl ?? scheduledCampaign.trackCoverUrl

  const { paypalEmail } = tiktokSchedulePaymentMethodProps

  const isWalletExpected = isWalletPaymentEligible && walletAmount > 0

  const {
    walletPaymentMethods,
    isWalletInitializing,
    onWalletAvailabilityChange,
    onChangePaymentMethod,
  } = useWalletPaymentMethods({
    onChange: tiktokSchedulePaymentMethodProps.onChange,
    canSelectWalletByDefault: !paypalEmail,
    isWalletExpected,
  })

  const displayedPaymentMethods = useMemo(
    () =>
      getDisplayedPaymentMethods({
        availablePaymentMethods,
        walletPaymentMethods,
        isOrderFullyCovered,
        paypalEmail,
      }),
    [
      availablePaymentMethods,
      walletPaymentMethods,
      isOrderFullyCovered,
      paypalEmail,
    ],
  )

  useEffect(() => {
    if (
      displayedPaymentMethods?.length &&
      !displayedPaymentMethods.includes(paymentMethod)
    ) {
      dispatch(
        setPaymentMethod(getFallbackPaymentMethod(displayedPaymentMethods)),
      )
    }
  }, [displayedPaymentMethods, paymentMethod, dispatch])

  const isConfirmDisabled = getIsConfirmDisabled(
    isWalletProcessing,
    paymentMethod,
    displayedPaymentMethods,
  )

  const platform = isReelsCampaign
    ? CampaignPlatformTypeEnum.REELS
    : CampaignPlatformTypeEnum.TIKTOK

  const onConfirmClick = (): void => {
    if (isReelsCampaign) {
      onReelsConfirmEvent()
      setIsReelsModalOpen(true)
      return
    }
    onConfirm?.()
  }

  const onCloseModal = (): void => {
    onReelsTikTokNotNow()
    setIsReelsModalOpen(false)
    navigate(ArtistRoutesEnum.CampaignsList)
  }

  const onTryTikTokClick = (): void => {
    onReelsTikTokTried()
    onUnsetReelsFeature()
    setIsReelsModalOpen(false)
  }

  const isCheckoutLoading = !availablePaymentMethods || isWalletInitializing

  const paymentMethodBlock = (
    <SchedulePaymentMethod
      {...tiktokSchedulePaymentMethodProps}
      isEnoughBalance={isOrderFullyCovered}
      onChange={onChangePaymentMethod}
      allowedPaymentMethods={displayedPaymentMethods}
      walletPaymentMethods={walletPaymentMethods}
      isLoading={isCheckoutLoading}
    />
  )

  const walletCheckout = isWalletExpected
    ? {
        amount: walletAmount,
        onWalletProcessingChange: setIsWalletProcessing,
        onConfirmWalletPayment,
        onWalletPaymentSuccess,
        onWalletPaymentError,
        onWalletAvailabilityChange,
      }
    : void 0

  return (
    <>
      {isProcessingPageVisible && <SchedulePaymentProcessingModal />}
      {paymentError && <ScheduleCheckoutErrors name={paymentError} />}
      {isReelsModalOpen && (
        <ScheduleReelsModal
          onTryTikTok={onTryTikTokClick}
          onCloseModal={onCloseModal}
        />
      )}
      <Wrapper>
        {paymentMethodBlock}
        <ScheduleSummary
          {...tiktokScheduleSummaryProps}
          onConfirm={onConfirmClick}
          platform={platform}
          confirmDisabled={isConfirmDisabled || isSyncing}
          paymentMethod={paymentMethod}
          trackCover={trackCover}
          isLoading={isCheckoutLoading}
          walletCheckout={walletCheckout}
        />
      </Wrapper>
    </>
  )
}
